"use no memo";

import type React from "react";

import { PlusIcon, TrashIcon, V2Button, V2TextField } from "@stacklet/ui";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import { useFragment } from "react-relay";
import { graphql } from "relay-runtime";

import { RolePicker } from "../RolePicker/RolePicker";
import { ScopePicker } from "../ScopePicker";
import { type GroupFormSchema, getDefaultRole } from "./GroupSchema";

import type { GroupFormFields_query$key } from "./__generated__/GroupFormFields_query.graphql";

/**
 * Fragment that combines role picker and scope picker data requirements
 */
export const GroupFormFieldsFragment = graphql`
  fragment GroupFormFields_query on Query {
    ...RolePicker_roles
    ...ScopePicker_scopes
  }
`;

interface Props {
  queryRef: GroupFormFields_query$key;
  /**
   * Make the name and display-name fields read-only. Set for SSO-provisioned
   * groups, whose metadata is owned by the IdP and can't be edited here.
   */
  metadataReadOnly?: boolean;
  /** Action buttons to render on the same row as "Add another role" */
  actionButtons?: React.ReactNode;
}

export function GroupFormFields({
  queryRef,
  metadataReadOnly = false,
  actionButtons,
}: Props) {
  const data = useFragment(GroupFormFieldsFragment, queryRef);

  const {
    control,
    watch,
    formState: { errors },
    setValue,
  } = useFormContext<GroupFormSchema>();

  const { fields, append, remove } = useFieldArray({
    control,
    name: "roles",
  });

  // Watch all roles to determine which ones are already selected
  const allRoles = watch("roles");

  return (
    <div className="flex flex-col gap-sm">
      <Controller
        control={control}
        name="name"
        render={({ field, fieldState }) => (
          <V2TextField
            assistiveText={
              fieldState.error?.message ||
              "Group name from your identity provider"
            }
            isInvalid={!!fieldState.error}
            isReadOnly={metadataReadOnly}
            label="Group Name (Case Sensitive)"
            {...field}
          />
        )}
      />

      <Controller
        control={control}
        name="displayName"
        render={({ field, fieldState }) => (
          <V2TextField
            assistiveText={
              fieldState.error?.message ||
              "A descriptive name if the group name isn't sufficient"
            }
            isInvalid={!!fieldState.error}
            isReadOnly={metadataReadOnly}
            label="Display Name"
            {...field}
          />
        )}
      />

      <div className="flex w-full flex-col gap-xs">
        <div className="text-title-small">Roles and Target</div>
        {fields.map((field, index) => {
          const selectedRole = watch(`roles.${index}.role`);
          // Get all selected roles except the current one to prevent duplicates
          const otherSelectedRoles =
            allRoles
              ?.map((role, i) => (i !== index ? role.role : null))
              .filter((role): role is string => Boolean(role)) || [];

          return (
            <div
              className="my-md flex w-full flex-row items-start justify-between gap-md"
              key={field.id}
            >
              <div className="min-w-0 flex-1">
                <div className="flex flex-col gap-md">
                  <div>
                    <RolePicker
                      disabledOptions={otherSelectedRoles}
                      onChange={(value) => {
                        setValue(`roles.${index}.role`, value);
                        setValue(
                          `roles.${index}.scope`,
                          value === "resource-viewer" ||
                            value === "policy-deployer"
                            ? "GRANT_ACCOUNTS_ALL"
                            : "GRANT_SYSTEM_ALL",
                          { shouldValidate: true, shouldDirty: true },
                        );
                      }}
                      rolesRef={data}
                      value={selectedRole}
                    />
                    {errors.roles?.[index]?.role && (
                      <p className="mt-xs text-label-small text-text-negative">
                        {errors.roles[index].role?.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <Controller
                      control={control}
                      name={`roles.${index}.scope`}
                      render={({ field: { onChange, value } }) => (
                        <ScopePicker
                          index={index}
                          onChange={onChange}
                          queryRef={data}
                          selectedRole={selectedRole}
                          value={value}
                        />
                      )}
                    />
                    {errors.roles?.[index]?.scope && (
                      <p className="mt-xs text-label-small text-text-negative">
                        {errors.roles[index].scope?.message}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {fields.length > 1 ? (
                <V2Button
                  IconLeft={<TrashIcon className="h-4 w-4" />}
                  aria-label="Remove role row"
                  className="mt-xl shrink-0"
                  onPress={() => remove(index)}
                  type="button"
                  variant="plain"
                />
              ) : null}
            </div>
          );
        })}

        {errors.roles?.message ? (
          <p className="mt-xs text-label-small text-text-negative">
            {errors.roles.message}
          </p>
        ) : null}

        <V2Button
          IconLeft={<PlusIcon className="h-4 w-4" />}
          className="w-fit"
          onPress={() => append(getDefaultRole())}
          type="button"
          variant="secondary"
        >
          Add another role
        </V2Button>

        {/* Action buttons on the same row */}
        {actionButtons ? (
          <div className="flex items-center justify-between gap-xs">
            <div />
            <div className="flex gap-xs">{actionButtons}</div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
